/*
 * App embeds kept out of the shopper's way (layout/theme.liquid loads this on every page).
 *
 * Shopify Inbox's chat button (<shopify-chat>): it sits over the bottom right corner, which on a
 * phone is where Add to bag lives. It rises above any bar pinned to the bottom (the product
 * page's Add to bag bar, a service page's action bar, the cookie banner) and steps aside while
 * something else has the screen: the bag, search, the menu, the filter sheet, the condition
 * guide, the certificate, Seel's pop-up, a photo viewer, the sign-up pop-up and the Saved drawer. The whole
 * see-through layer moves (luxe.css), and while a chat is open it stays where Shopify puts it.
 *
 * The theme's own floating bits ([data-ldj-float]: the sign-up offer's tab) follow the same
 * rule: lifted above those bars, faded while something else has the screen.
 *
 * Neither ever sits on the buttons that sell: Add to bag (with the spa box and Buy it now) and
 * the bag page's checkout (with its terms box and the express buttons). The page scrolls under
 * both (on larger screens the chat floats over the page itself), so while one of those passes
 * under the chat or the tab, it fades out of the way.
 *
 * Klaviyo's own sign-up pop-up: when the theme's pop-up is set up to sign people up to the same
 * Klaviyo lists (snippets/newsletter-popup.liquid adds html.ldj-own-popup), Klaviyo's pop-up and
 * its "Up to $300 Off" tab are hidden (luxe.css) and the scroll lock it puts on the page is taken
 * off again. Klaviyo's forms placed in a page (the scam guide's download) are untouched.
 */
(() => {
  'use strict';

  if (window.LdjEmbeds) return;
  window.LdjEmbeds = true;

  const root = document.documentElement;

  // ---------------------------------------------------------------- Klaviyo's pop-up

  const KLAVIYO_LOCK = 'klaviyo-prevent-body-scrolling';
  const ownPopup = () => root.classList.contains('ldj-own-popup');
  const unlock = () => {
    if (ownPopup() && document.body?.classList.contains(KLAVIYO_LOCK)) document.body.classList.remove(KLAVIYO_LOCK);
  };
  if (document.body) {
    new MutationObserver(unlock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      new MutationObserver(unlock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }, { once: true });
  }
  window.addEventListener('klaviyoForms', unlock);

  // ---------------------------------------------------------------- the chat button

  // Something that has the whole screen, or the bottom of it.
  const BUSY_CLASSES = ['bag-lock', 'sov-open', 'search-lock', 'menu-lock', 'luxe-search-filters-open', 'pdp-cond-open', 'pdp-pass-open', 'ldj-popup-open', 'ldj-saved-open', 'ldj-seel-open'];
  const GAP = 12;

  const busy = () => BUSY_CLASSES.some((name) => root.classList.contains(name))
    || Boolean(document.querySelector('.pswp--open, dialog.ldj-popup[open]'));

  // How far a transform moves an element up or down right now (mid-move included), to find
  // where it comes to rest.
  const shiftOf = (element) => {
    const transform = getComputedStyle(element).transform;
    if (!transform || transform === 'none') return 0;
    try {
      return new DOMMatrixReadOnly(transform).m42;
    } catch (error) {
      return 0;
    }
  };

  // Bars pinned to the bottom of the screen that the button must clear, the cookie banner
  // among them (the chat stays reachable while a shopper leaves the banner unanswered).
  const BARS = '.pdp-bar, [data-svc-dock], [data-ldj-bottom-bar], #shopify-pc__banner';
  const barTop = () => {
    let top = Infinity;
    const viewport = window.innerHeight;
    document.querySelectorAll('.pdp-bar.is-visible, body.svc-dock-open [data-svc-dock], [data-ldj-bottom-bar], #shopify-pc__banner').forEach((bar) => {
      const box = bar.getBoundingClientRect();
      const style = getComputedStyle(bar);
      // Where it comes to rest: a bar sliding back in was still below the screen when this
      // ran, so the chat stayed down and sat on the bar's own Add to bag.
      const shift = shiftOf(bar);
      const barTopAt = box.top - shift;
      const barBottomAt = box.bottom - shift;
      // Only a bar held at the foot of the screen: on larger screens the product page's bar
      // sits under the header, and the chat rose all the way up to it.
      if (box.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && barTopAt < viewport && barBottomAt > viewport * 0.6) top = Math.min(top, barTopAt);
    });
    return top;
  };

  let chat = null;
  let queued = false;
  let lastLift = -1;
  let lastAside = null;
  let chatDrawn = false;
  let chatButton = null;
  const resized = 'ResizeObserver' in window ? new ResizeObserver(() => queue()) : null;

  // ---------------------------------------------------------------- the buttons that sell

  const KEEP_CLEAR = [
    '.pdp__buy .pdp__atc',
    '.pdp__buy .pdp__spa',
    '.pdp__buy .shopify-payment-button',
    '[data-pdp-bar-cta]',
    '.bag-page .bag-terms',
    '.bag-page [data-bag-checkout-button]',
    '.bag-page [data-bag-express]',
    '.svc-book__button',
  ].join(', ');
  const CLEARANCE = 8;
  // The buttons on this page, and each floating thing's box once lifted (none while something
  // else has the screen: it is out of the way already).
  let buttons = [];
  const spots = new Map();

  const covers = (box, rect) => rect.width > 0 && rect.height > 0
    && rect.right > box.left - CLEARANCE && rect.left < box.right + CLEARANCE
    && rect.bottom > box.top - CLEARANCE && rect.top < box.bottom + CLEARANCE;

  // It fades (luxe.css), and stops catching taps at once: inert lets a tap through to Add to
  // bag underneath while the button is still fading.
  const setAside = (aside) => {
    if (!chat || aside === lastAside) return;
    chat.toggleAttribute('data-ldj-aside', aside);
    chat.toggleAttribute('inert', aside);
    lastAside = aside;
  };

  // Out of the way while one of the buttons is under it.
  const clearButtons = () => {
    const rects = buttons.map((button) => button.getBoundingClientRect());
    spots.forEach((box, owner) => {
      const aside = rects.some((rect) => covers(box, rect));
      if (owner === chat) setAside(aside);
      else owner.toggleAttribute('data-ldj-float-hidden', aside);
    });
  };

  // Only pages with such buttons do anything while they scroll: one check a frame, reading
  // where the buttons are (no writes unless something has to step aside or come back).
  let scrollQueued = false;
  window.addEventListener('scroll', () => {
    if (scrollQueued || !buttons.length || !spots.size) return;
    scrollQueued = true;
    window.requestAnimationFrame(() => {
      scrollQueued = false;
      clearButtons();
    });
  }, { passive: true });

  // The lift that clears the bars, and the box the thing has once lifted.
  const lifted = (box, shift, top) => {
    const rest = { top: box.top - shift, right: box.right, bottom: box.bottom - shift, left: box.left };
    const lift = top === Infinity ? 0 : Math.max(0, Math.ceil(rest.bottom - (top - GAP)));
    return { lift, box: { ...rest, top: rest.top - lift, bottom: rest.bottom - lift } };
  };

  const place = () => {
    queued = false;
    // Nothing floating yet (the chat arrives after the page): nothing to measure. While a page
    // loads this is called again and again, and each measure is a full layout on a busy page.
    if (!chat && !document.querySelector('[data-ldj-float]:not([hidden])')) {
      buttons = [];
      spots.clear();
      return;
    }
    const isBusy = busy();
    const top = barTop();
    buttons = Array.from(document.querySelectorAll(KEEP_CLEAR));
    spots.clear();

    // Things of the theme's own that float at the bottom (the sign-up offer's tab): the same rule.
    document.querySelectorAll('[data-ldj-float]:not([hidden])').forEach((element) => {
      const spot = lifted(element.getBoundingClientRect(), shiftOf(element), top);
      if (String(spot.lift) !== element.dataset.ldjLift) {
        element.style.setProperty('--ldj-float-lift', `${spot.lift}px`);
        element.dataset.ldjLift = String(spot.lift);
      }
      if (isBusy) element.setAttribute('data-ldj-float-hidden', '');
      else spots.set(element, spot.box);
    });

    if (chat) {
      // Shopify draws the button into the chat's own shadow root after the page, and redraws it
      // now and then: each change, or a change in its size, places it again.
      if (!chatDrawn && chat.shadowRoot) {
        chatDrawn = true;
        new MutationObserver(queue).observe(chat.shadowRoot, { childList: true, subtree: true });
      }
      const button = chat.shadowRoot?.querySelector('[part~="activator"], button');
      if (button !== chatButton && resized) {
        if (chatButton) resized.unobserve(chatButton);
        if (button) resized.observe(button);
        chatButton = button;
      }
      const box = button?.getBoundingClientRect();
      // An open chat is the shopper's: leave it exactly where it is.
      if (chat.hasAttribute('open')) setAside(false);
      else if (isBusy) setAside(true);
      else if (!box || !box.width) setAside(false);
      else {
        const spot = lifted(box, shiftOf(chat), top);
        if (spot.lift !== lastLift) {
          chat.style.setProperty('--ldj-chat-lift', `${spot.lift}px`);
          lastLift = spot.lift;
        }
        spots.set(chat, spot.box);
      }
    }

    clearButtons();
  };

  const queue = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(place);
  };

  const watch = (element) => {
    chat = element;
    new MutationObserver(queue).observe(chat, { attributes: true, attributeFilter: ['open'] });
    // Its shadow root comes with Shopify's script, which may load after this one.
    window.customElements?.whenDefined('shopify-chat').then(queue);
    queue();
  };

  // Changes that can move a bar or claim the screen: classes on <html> and <body>, the product
  // page's bar showing or hiding, dialogs opening, the cookie banner going away, a resize.
  new MutationObserver(queue).observe(root, { attributes: true, attributeFilter: ['class'] });
  const watchBanner = (banner) => {
    if (!banner || banner.dataset.ldjWatched !== undefined) return;
    banner.dataset.ldjWatched = '';
    new MutationObserver(queue).observe(banner, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  };

  const bodyWatch = () => {
    new MutationObserver(queue).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // The chat and the cookie banner arrive after the page; both are added to <body>.
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (!chat && node.matches?.('shopify-chat')) watch(node);
          if (node.matches?.('[data-ldj-float]')) new MutationObserver(queue).observe(node, { attributes: true, attributeFilter: ['hidden'] });
          if (node.id === 'shopify-pc__banner') watchBanner(node);
          else watchBanner(node.querySelector?.('#shopify-pc__banner'));
        }
      }
      queue();
    }).observe(document.body, { childList: true });
    document.querySelectorAll('.pdp-bar, [data-svc-dock], [data-ldj-bottom-bar]').forEach((bar) => {
      new MutationObserver(queue).observe(bar, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    });
    watchBanner(document.getElementById('shopify-pc__banner'));
    document.addEventListener('close', queue, true);
    // The page growing or shrinking (photos, a note opening) moves the buttons without a scroll.
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(document.body);
    const existing = document.querySelector('shopify-chat');
    if (existing) watch(existing);
  };
  if (document.body) bodyWatch();
  else document.addEventListener('DOMContentLoaded', bodyWatch, { once: true });

  // A bar done sliding in or out. A resize, and again once the page (and the chat, which
  // redraws for the new size) has settled.
  document.addEventListener('transitionend', (event) => {
    if (event.target.matches?.(BARS)) queue();
  }, true);
  let settle = 0;
  window.addEventListener('resize', () => {
    queue();
    window.clearTimeout(settle);
    settle = window.setTimeout(queue, 400);
  }, { passive: true });
  // The cookie banner goes once the shopper answers it; the bag page draws its summary anew.
  document.addEventListener('visitorConsentCollected', queue);
  document.addEventListener('bag:changed', queue);
  window.setTimeout(queue, 3000);
})();
